import { IChartApi, ISeriesApi, LineSeries } from 'lightweight-charts';
import { observer } from 'mobx-react-lite';

import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { useCallback, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { StockIndexPriceResponse } from '../../../server/response/responseSchema';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import StockChart, { StockChartRef } from '../chart/StockChart';
import ChangeValue from '../common/ChangeValue';

class ViewStore {
    goldData: StockIndexPriceResponse[] = [];
    btcData: StockIndexPriceResponse[] = [];
    type: 'gold' | 'btc' = 'btc';
    isLoading: boolean = true;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        const [goldResponse, btcResponse] = await Promise.all([
            apiClient.stockIndex.public.getValuesInRange.$query({
                ticker: '_GOLD',
            }),
            apiClient.stockIndex.public.getValuesInRange.$query({
                ticker: '_BTC',
            }),
        ]);

        runInAction(() => {
            this.goldData = goldResponse;
            this.btcData = btcResponse;
            this.isLoading = false;
        });
    }

    changeType(type: 'gold' | 'btc') {
        this.type = type;
    }
}

const GOLD_COLOR = '#ffc259';
const BTC_COLOR = '#6f86d6';

const GoldBtcBriefCard = observer(({ className }: { className?: string }) => {
    const viewStore = useMobxInstance(ViewStore);

    const chartRef = useRef<StockChartRef>(null);
    const currentSeriesRef = useRef<ISeriesApi<any> | null>(null);

    const redrawSeries = useCallback(() => {
        const chart = chartRef.current?.getChartApi();
        if (!chart) {
            return;
        }

        if (currentSeriesRef.current) {
            chart.removeSeries(currentSeriesRef.current);
        }

        if (viewStore.type === 'gold') {
            const goldSeries = chart.addSeries(LineSeries, {
                lineWidth: 2,
                priceScaleId: 'right',
                color: GOLD_COLOR,
            });

            goldSeries.setData(
                _.map(viewStore.goldData, (value) => ({
                    time: value.date,
                    value: value.value,
                })),
            );

            chart.timeScale().setVisibleLogicalRange({
                from: viewStore.goldData.length - 80,
                to: viewStore.goldData.length,
            });

            currentSeriesRef.current = goldSeries;
        } else if (viewStore.type === 'btc') {
            const btcSeries = chart.addSeries(LineSeries, {
                lineWidth: 2,
                priceScaleId: 'right',
                color: BTC_COLOR,
            });

            btcSeries.setData(
                _.map(viewStore.btcData, (value) => ({
                    time: value.date,
                    value: value.value,
                })),
            );

            chart.timeScale().setVisibleLogicalRange({
                from: viewStore.btcData.length - 80,
                to: viewStore.btcData.length,
            });

            currentSeriesRef.current = btcSeries;
        }
    }, [viewStore.type]);

    const onChartReady = async (chart: IChartApi) => {
        await viewStore.fetch();
    };

    useEffect(() => {
        if (chartRef.current && !viewStore.isLoading) {
            redrawSeries();
        }
    }, [viewStore.type, viewStore.isLoading]);

    const lastGoldPoint = _.nth(viewStore.goldData, -1);
    const prevGoldPoint = _.nth(viewStore.goldData, -2);
    const goldChangePercent =
        lastGoldPoint?.value && prevGoldPoint?.value
            ? ((lastGoldPoint.value - prevGoldPoint.value) / prevGoldPoint.value) * 100
            : 0;

    const lastBtcPoint = _.nth(viewStore.btcData, -1);
    const prevBtcPoint = _.nth(viewStore.btcData, -2);
    const btcChangePercent =
        lastBtcPoint?.value && prevBtcPoint?.value
            ? ((lastBtcPoint.value - prevBtcPoint.value) / prevBtcPoint.value) * 100
            : 0;

    return (
        <div className={twMerge('flex grow flex-col', className)}>
            <div className="-mx-5">
                <StockChart ref={chartRef} onChartReady={onChartReady} />
            </div>
            {viewStore.type === 'gold' && lastGoldPoint && (
                <div className="mt-4">
                    <div className="text-base-content/80 flex flex-col text-xl">
                        <div>
                            <span style={{ color: GOLD_COLOR }} className="text-2xl font-bold">
                                금
                            </span>
                            은
                        </div>
                        <div className="mt-3">
                            온스 당
                            <strong className="mr-2 ml-2 text-3xl font-semibold">
                                {lastGoldPoint.value?.toFixed(2)}달러
                            </strong>
                            로
                        </div>
                        <div className="mt-3">
                            <ChangeValue
                                className="mr-2 text-3xl font-semibold"
                                value={goldChangePercent}
                                sign={true}
                                round={2}
                                suffix="%"
                            />
                            {goldChangePercent > 0 ? '상승' : '하락'}했습니다.
                        </div>
                    </div>
                </div>
            )}
            {viewStore.type === 'btc' && (
                <div className="mt-4">
                    {lastBtcPoint && (
                        <div className="text-base-content/80 flex flex-col text-xl">
                            <div>
                                <span style={{ color: BTC_COLOR }} className="text-2xl font-bold">
                                    비트코인
                                </span>
                                은
                            </div>
                            <div className="mt-3">
                                1개 당
                                <strong className="mr-2 ml-2 text-3xl font-semibold">
                                    {lastBtcPoint.value?.toFixed(2)}달러
                                </strong>
                                로
                            </div>
                            <div className="mt-3">
                                <ChangeValue
                                    className="mr-2 text-3xl font-semibold"
                                    value={btcChangePercent}
                                    sign={true}
                                    round={2}
                                    suffix="%"
                                />
                                {btcChangePercent > 0 ? '상승' : '하락'}했습니다.
                            </div>
                        </div>
                    )}
                </div>
            )}
            <div className="mt-8 flex items-center gap-3">
                <button
                    className={twMerge(
                        'btn text-base-content/40 rounded-full border',
                        viewStore.type === 'btc' &&
                            'btn-soft bg-primary/10 text-primary border-primary',
                    )}
                    onClick={() => viewStore.changeType('btc')}
                >
                    비트코인
                </button>
                <button
                    className={twMerge(
                        'btn text-base-content/40 rounded-full border',
                        viewStore.type === 'gold' &&
                            'btn-soft bg-primary/10 text-primary border-primary',
                    )}
                    onClick={() => viewStore.changeType('gold')}
                >
                    금
                </button>
            </div>
        </div>
    );
});

export default GoldBtcBriefCard;
